import { useState, useEffect, useMemo, useCallback } from "react";
import { get } from "lodash-es";
import { getMomentTz } from "@dexteel/mesf-core";
import { getUsageStatistics } from "../repositories/usage-statistics-repository";
import type {
	StatisticsFilter,
	ComparisonFilter,
} from "../models/usage-statistic";

const moment = getMomentTz();

export const useSearchUsageStatistics = () => {
	const [statistics, setStatistics] = useState<any[]>([]);
	const [comparisonStatistics, setComparisonStatistics] = useState<any[]>([]);
	const [loading, setLoading] = useState(true);
	const [comparisonLoading, setComparisonLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [comparisonError, setComparisonError] = useState<string | null>(null);
	const [dateValidationErrors, setDateValidationErrors] = useState({
		startDateError: false,
		endDateError: false,
		comparisonStartDateError: false,
		comparisonEndDateError: false,
	});
	const [searchText, setSearchText] = useState<string>("");
	const [isComparisonMode, setIsComparisonMode] = useState(false);
	const [filter, setFilter] = useState<StatisticsFilter>({
		start: moment().subtract(31, "days").toDate(),
		end: moment().toDate(),
	});
	const [comparisonFilter, setComparisonFilter] = useState<ComparisonFilter>({
		start: moment().subtract(62, "days").toDate(),
		end: moment().subtract(31, "days").toDate(),
	});

	const searchUsageStatistics = useCallback(
		async (currentFilter?: StatisticsFilter) => {
			setLoading(true);
			setError(null);

			const filterToUse = currentFilter || filter;
			const res = await getUsageStatistics(filterToUse);

			if (res.ok) {
				const rowsSource = get(res, "data.tables[0].rows", []);
				setStatistics(rowsSource);
			} else {
				setError(res.message || "Failed to fetch usage statistics");
			}

			setLoading(false);
		},
		[filter],
	);

	const searchComparisonStatistics = useCallback(
		async (currentFilter?: ComparisonFilter) => {
			setComparisonLoading(true);
			setComparisonError(null);

			const filterToUse = currentFilter || comparisonFilter;
			const res = await getUsageStatistics(filterToUse);

			if (res.ok) {
				const rowsSource = get(res, "data.tables[0].rows", []);
				setComparisonStatistics(rowsSource);
			} else {
				setComparisonError(
					res.message || "Failed to fetch comparison statistics",
				);
			}

			setComparisonLoading(false);
		},
		[comparisonFilter],
	);

	const validateDates = useCallback(
		(start: Date, end: Date, isComparison = false) => {
			const today = moment().endOf("day");
			const startMoment = moment(start);
			const endMoment = moment(end);

			const errors = {
				startDateError: false,
				endDateError: false,
			};

			if (endMoment.isAfter(today)) {
				errors.endDateError = true;
			}

			if (startMoment.isAfter(today)) {
				errors.startDateError = true;
			}

			if (startMoment.isSameOrAfter(endMoment)) {
				errors.startDateError = true;
				errors.endDateError = true;
			}

			return errors;
		},
		[],
	);

	const updateFilter = useCallback(
		(newFilter: Partial<StatisticsFilter>) => {
			const updatedFilter = { ...filter, ...newFilter };
			const validationErrors = validateDates(
				updatedFilter.start,
				updatedFilter.end,
			);

			setDateValidationErrors((prev) => ({
				...prev,
				startDateError: validationErrors.startDateError,
				endDateError: validationErrors.endDateError,
			}));

			if (validationErrors.startDateError || validationErrors.endDateError) {
				if (updatedFilter.start >= updatedFilter.end) {
					setError("Start date must be before end date");
				} else if (moment(updatedFilter.end).isAfter(moment().endOf("day"))) {
					setError("End date cannot be in the future");
				} else if (moment(updatedFilter.start).isAfter(moment().endOf("day"))) {
					setError("Start date cannot be in the future");
				}
				return;
			}

			setError(null);
			setFilter(updatedFilter);
			searchUsageStatistics(updatedFilter);
		},
		[filter, searchUsageStatistics, validateDates],
	);

	const updateComparisonFilter = useCallback(
		(newFilter: Partial<ComparisonFilter>) => {
			const updatedFilter = { ...comparisonFilter, ...newFilter };
			const validationErrors = validateDates(
				updatedFilter.start,
				updatedFilter.end,
				true,
			);

			setDateValidationErrors((prev) => ({
				...prev,
				comparisonStartDateError: validationErrors.startDateError,
				comparisonEndDateError: validationErrors.endDateError,
			}));

			if (validationErrors.startDateError || validationErrors.endDateError) {
				if (updatedFilter.start >= updatedFilter.end) {
					setComparisonError("Comparison start date must be before end date");
				} else if (moment(updatedFilter.end).isAfter(moment().endOf("day"))) {
					setComparisonError("Comparison end date cannot be in the future");
				} else if (moment(updatedFilter.start).isAfter(moment().endOf("day"))) {
					setComparisonError("Comparison start date cannot be in the future");
				}
				return;
			}

			setComparisonError(null);
			setComparisonFilter(updatedFilter);
			if (isComparisonMode) {
				searchComparisonStatistics(updatedFilter);
			}
		},
		[
			comparisonFilter,
			isComparisonMode,
			searchComparisonStatistics,
			validateDates,
		],
	);

	const toggleComparisonMode = () => {
		const newMode = !isComparisonMode;
		setIsComparisonMode(newMode);

		if (newMode) {
			const newComparisonFilter: ComparisonFilter = {
				start: moment().subtract(31, "days").toDate(),
				end: moment().toDate(),
			};
			const newFilter: StatisticsFilter = {
				start: moment().subtract(62, "days").toDate(),
				end: moment().subtract(31, "days").toDate(),
			};

			setComparisonFilter(newComparisonFilter);
			setFilter(newFilter);
			searchComparisonStatistics(newComparisonFilter);
			searchUsageStatistics(newFilter);
		} else {
			const normalFilter: StatisticsFilter = {
				start: moment().subtract(31, "days").toDate(),
				end: moment().toDate(),
			};
			setFilter(normalFilter);
			searchUsageStatistics(normalFilter);
		}
	};

	const resetFilters = () => {
		setDateValidationErrors({
			startDateError: false,
			endDateError: false,
			comparisonStartDateError: false,
			comparisonEndDateError: false,
		});
		setError(null);
		setComparisonError(null);

		if (isComparisonMode) {
			const defaultComparisonFilter: ComparisonFilter = {
				start: moment().subtract(31, "days").toDate(),
				end: moment().toDate(),
			};
			const defaultFilter: StatisticsFilter = {
				start: moment().subtract(62, "days").toDate(),
				end: moment().subtract(31, "days").toDate(),
			};

			setFilter(defaultFilter);
			setComparisonFilter(defaultComparisonFilter);
			setSearchText("");
			searchUsageStatistics(defaultFilter);
			searchComparisonStatistics(defaultComparisonFilter);
		} else {
			const defaultFilter: StatisticsFilter = {
				start: moment().subtract(31, "days").toDate(),
				end: moment().toDate(),
			};

			setFilter(defaultFilter);
			setSearchText("");
			searchUsageStatistics(defaultFilter);
		}
	};

	const filteredStatistics = useMemo(() => {
		if (!searchText.trim()) {
			return statistics;
		}

		return statistics.filter((stat) =>
			stat.Metric?.toLowerCase().includes(searchText.toLowerCase()),
		);
	}, [statistics, searchText]);

	const filteredComparisonStatistics = useMemo(() => {
		if (!searchText.trim()) {
			return comparisonStatistics;
		}

		return comparisonStatistics.filter((stat) =>
			stat.Metric?.toLowerCase().includes(searchText.toLowerCase()),
		);
	}, [comparisonStatistics, searchText]);

	const updateSearchText = (text: string) => {
		setSearchText(text);
	};

	useEffect(() => {
		const initialFilter: StatisticsFilter = {
			start: moment().subtract(31, "days").toDate(),
			end: moment().toDate(),
		};
		searchUsageStatistics(initialFilter);
	}, []);

	return {
		statistics: filteredStatistics,
		comparisonStatistics: filteredComparisonStatistics,
		loading,
		comparisonLoading,
		error,
		comparisonError,
		filter,
		comparisonFilter,
		searchText,
		isComparisonMode,
		dateValidationErrors,
		refetch: () => searchUsageStatistics(),
		refetchComparison: () => searchComparisonStatistics(),
		updateFilter,
		updateComparisonFilter,
		toggleComparisonMode,
		resetFilters,
		updateSearchText,
	};
};
